import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, Typography,
} from '@mui/material';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { format } from 'date-fns';
import type { ProductionLineConfig } from '../types/industrialCost';
import type { ProductionIntelligenceOrder } from '../types/productionIntelligence';
import { getProductionIntelligenceMaterialTraceability } from '../services/productionIntelligenceService';
import IndustrialCostProductionInsightsPanel from './IndustrialCostProductionInsightsPanel';

import { esNumberFormat } from '../utils/numberFormat';
type Props = { configuredLines: ProductionLineConfig[] };
type StatusFilter = 'all' | 'calculable' | 'blocked';

const fmt = (value: number | null | undefined): string =>
  value == null ? '—' : esNumberFormat({ maximumFractionDigits: 3 }).format(value);

const cardSx = {
  bgcolor: '#0b1220',
  border: '1px solid rgba(148,163,184,0.18)',
  borderRadius: 2.5,
};

export default function IndustrialCostProductionIntelligenceView({ configuredLines }: Props) {
  const [referenceDate, setReferenceDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [lineId, setLineId] = useState('');
  const [limit, setLimit] = useState(50);
  const [orderSearch, setOrderSearch] = useState('');
  const [requested, setRequested] = useState<{
    reference_date: string;
    production_line_config_id?: string;
    limit: number;
    order_search?: string;
  } | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [issueFilter, setIssueFilter] = useState('all');
  const [expanded, setExpanded] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const query = useQuery({
    queryKey: ['production-intelligence-traceability', requested],
    queryFn: () => getProductionIntelligenceMaterialTraceability(requested!),
    enabled: requested !== null,
    retry: false,
  });
  const response = query.data;

  const diagnostics = useMemo(() => {
    const issueMap = new Map<string, {
      code: string;
      message: string;
      count: number;
      examples: string[];
    }>();

    const sourceUnitMap = new Map<string, number>();
    const catalogUnitMap = new Map<string, number>();

    for (const row of response?.rows ?? []) {
      const sourceUnit =
        row.input_trace?.source_quantity_unit?.trim() || 'Sin informar';

      const catalogUnit =
        row.input_trace?.catalog_quantity_unit?.trim() || 'No disponible';

      sourceUnitMap.set(
        sourceUnit,
        (sourceUnitMap.get(sourceUnit) ?? 0) + 1,
      );

      catalogUnitMap.set(
        catalogUnit,
        (catalogUnitMap.get(catalogUnit) ?? 0) + 1,
      );

      const seenCodes = new Set<string>();

      for (const issue of row.issues) {
        if (seenCodes.has(issue.code)) continue;
        seenCodes.add(issue.code);

        const existing = issueMap.get(issue.code) ?? {
          code: issue.code,
          message: issue.message,
          count: 0,
          examples: [],
        };

        existing.count += 1;

        if (
          row.manufacturing_order_number &&
          existing.examples.length < 3
        ) {
          existing.examples.push(row.manufacturing_order_number);
        }

        issueMap.set(issue.code, existing);
      }
    }

    return {
      issues: Array.from(issueMap.values()).sort(
        (a, b) => b.count - a.count,
      ),
      sourceUnits: Array.from(sourceUnitMap.entries()).sort(
        (a, b) => b[1] - a[1],
      ),
      catalogUnits: Array.from(catalogUnitMap.entries()).sort(
        (a, b) => b[1] - a[1],
      ),
    };
  }, [response]);

  const unresolvedBreakdown = useMemo(() => {
    const groups = new Map<string, {
      label: string;
      count: number;
      examples: string[];
    }>();

    for (const row of response?.rows ?? []) {
      if (!row.issues.some(issue => issue.code === 'unresolved_product')) {
        continue;
      }

      const source = row.input_trace?.source_type === 'imported_order'
        ? 'OF importada'
        : row.input_trace?.source_type === 'open_order'
          ? 'Parte de producción'
          : 'Origen no identificado';

      const reference = (
        row.input_trace?.source_product_reference ||
        row.product_ref ||
        ''
      ).trim();

      const reason = reference
        ? 'Referencia informada sin vinculación'
        : 'Referencia de producto ausente';

      const label = `${source} · ${reason}`;

      const group = groups.get(label) ?? {
        label,
        count: 0,
        examples: [],
      };

      group.count += 1;

      if (group.examples.length < 4) {
        group.examples.push(
          `${row.manufacturing_order_number || 'OF sin número'} → ${reference || 'Sin referencia'}`
        );
      }

      groups.set(label, group);
    }

    return Array.from(groups.values()).sort(
      (a, b) => b.count - a.count,
    );
  }, [response]);

  const rows = useMemo(() => (response?.rows ?? []).filter(row => {
    if (statusFilter !== 'all' && row.status !== statusFilter) return false;

    if (
      issueFilter !== 'all' &&
      !row.issues.some(issue => issue.code === issueFilter)
    ) {
      return false;
    }

    const term = search.trim().toLocaleLowerCase('es');

    return !term || [
      row.manufacturing_order_number,
      row.product_ref,
      row.product_code,
      ...row.materials.map(
        material =>
          `${material.material_code ?? ''} ${material.material_name ?? ''}`,
      ),
      ...row.issues.map(issue => issue.message),
    ].join(' ').toLocaleLowerCase('es').includes(term);
  }), [response, statusFilter, issueFilter, search]);

  const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);

  const visibleRows = rows.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );

  const runAudit = () => {
    if (!referenceDate) return;

    const appliedOrderSearch = orderSearch.trim() || undefined;

    setExpanded([]);
    setPage(0);
    setSearch('');
    setIssueFilter('all');
    setStatusFilter('all');

    setRequested({
      reference_date: referenceDate,
      production_line_config_id: lineId || undefined,
      limit,
      order_search: appliedOrderSearch,
    });

    if (
      requested?.reference_date === referenceDate &&
      requested?.production_line_config_id === (lineId || undefined) &&
      requested?.limit === limit &&
      requested?.order_search === appliedOrderSearch
    ) {
      void query.refetch();
    }
  };
  const toggle = (id: string) => setExpanded(previous => previous.includes(id)
    ? previous.filter(item => item !== id) : [...previous, id]);
  const detail = (row: ProductionIntelligenceOrder) => (
    <Stack spacing={1.4} sx={{ p: 2, bgcolor: 'rgba(2,6,23,0.34)', borderRadius: 2 }}>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip size="small" variant="outlined" label={`Producto: ${row.product_code || 'No resuelto'}`} />
        <Chip size="small" variant="outlined" label={`Cantidad: ${fmt(row.quantity_pending)} ${row.quantity_unit || ''}`} />
        {row.consumption_basis && <Chip size="small" variant="outlined"
          label={`Base: ${row.consumption_basis === 'per_kg' ? 'por kg fabricado' : 'por unidad fabricada'}`} />}
      </Stack>
      {row.input_trace && (
        <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
          <Typography sx={{ color: '#7dd3fc', fontWeight: 850, fontSize: '0.8rem', mb: 1 }}>
            Trazabilidad de los datos originales
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} flexWrap="wrap">
            <Box sx={{ minWidth: 150 }}>
              <Typography variant="caption" color="text.secondary">
                Origen de la orden
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.83rem' }}>
                {row.input_trace.source_type === 'stock_of_snapshot'
                  ? 'Snapshot de OF pendientes'
                  : row.input_trace.source_type === 'open_order'
                    ? 'Parte de producción'
                    : row.input_trace.source_type === 'imported_order'
                      ? 'OF importada'
                      : 'Origen no identificado'}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 150 }}>
              <Typography variant="caption" color="text.secondary">
                Referencia recibida
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.83rem' }}>
                {row.input_trace.source_product_reference || 'Sin informar'}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                Unidad original de la OF
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.83rem' }}>
                {row.input_trace.source_quantity_unit || 'Sin informar'}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                Unidad del catálogo
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.83rem' }}>
                {row.input_trace.catalog_quantity_unit || 'No disponible'}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                Producto vinculado directamente
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.83rem' }}>
                {row.input_trace.has_direct_product_link ? 'Sí' : 'No'}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {row.issues.length > 0 && (
        <Stack spacing={0.65}>
          {row.issues.map(issue => <Alert key={`${issue.code}-${issue.message}`} severity="warning" variant="outlined"
            sx={{ bgcolor: 'rgba(120,53,15,0.1)', py: 0.1 }}>
            {issue.message}
          </Alert>)}
        </Stack>
      )}
      {row.materials.length > 0 && (
        <TableContainer sx={{ ...cardSx, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Materia prima</TableCell>
              <TableCell>Regla</TableCell>
              <TableCell align="right">Cantidad por base</TableCell>
              <TableCell align="right">Necesidad técnica</TableCell>
            </TableRow></TableHead>
            <TableBody>{row.materials.map((material, i) => <TableRow key={`${material.rule_id ?? material.material_id}-${i}`}>
              <TableCell><Typography sx={{ fontWeight: 750, fontSize: '0.82rem' }}>
                {material.material_code || material.material_name || 'Sin identificar'}
              </Typography><Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>{material.material_name}</Typography></TableCell>
              <TableCell><Chip size="small" variant="outlined" label={material.rule_scope === 'line' ? 'Máquina' : 'General'} /></TableCell>
              <TableCell align="right">{fmt(material.quantity_per_base)} {material.unit_label || ''}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#7dd3fc' }}>
                {fmt(material.required_quantity)} {material.unit_label || ''}
              </TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>
      )}
      {row.materials.length === 0 && <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
        No hay necesidades materiales calculadas para esta orden.
      </Typography>}
      {row.status === 'blocked' && row.materials.length > 0 && <Typography sx={{ color: '#fbbf24', fontSize: '0.75rem' }}>
        Los materiales mostrados son parciales y no se deben utilizar como una necesidad completa de la orden.
      </Typography>}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <IndustrialCostProductionInsightsPanel
        referenceDate={referenceDate}
        configuredLines={configuredLines}
      />

      <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(15,23,42,0.58)', border: '1px solid rgba(148,163,184,0.16)', borderRadius: 3 }}>
        <Stack spacing={1.8}>
          <Stack direction="row" gap={1.25} alignItems="center">
            <AnalyticsRoundedIcon sx={{ color: '#38bdf8' }} />
            <Box>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.1rem' }}>Inteligencia de producción · Trazabilidad</Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                Auditoría experimental de relaciones entre órdenes pendientes, productos y escandallos.
              </Typography>
            </Box>
          </Stack>
          <Alert severity="info" variant="outlined" sx={{ bgcolor: 'rgba(56,189,248,0.055)' }}>
            Esta fase calcula únicamente necesidades técnicas según las recetas vigentes. No comprueba stock disponible,
            compatibilidad física de máquinas ni fechas de entrega; tampoco modifica pedidos ni planificación.
          </Alert>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={1.2} alignItems={{ md: 'center' }}>
            <TextField size="small" label="Fecha de referencia" type="date" value={referenceDate}
              onChange={event => setReferenceDate(event.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 190, '& input': { colorScheme: 'dark' } }} />
            <TextField select size="small" label="Máquina del escenario" value={lineId}
              onChange={event => setLineId(event.target.value)} sx={{ minWidth: { xs: '100%', md: 245 }, flexGrow: 1 }}>
              <MenuItem value="">Sin máquina asignada</MenuItem>
              {configuredLines.map(line => <MenuItem key={line.id} value={line.id}>
                {line.name}{line.is_active === false ? ' (inactiva)' : ''}
              </MenuItem>)}
            </TextField>
            <TextField size="small" select label="Órdenes a revisar" value={limit}
              onChange={event => setLimit(Number(event.target.value))} sx={{ width: { xs: '100%', md: 165 } }}>
              {[25, 50, 100, 200].map(n => <MenuItem key={n} value={n}>{n} órdenes</MenuItem>)}
            </TextField>
            <TextField
              size="small"
              label="Buscar OF o referencia en todas las órdenes"
              value={orderSearch}
              onChange={(event) => setOrderSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  runAudit();
                }
              }}
              sx={{ minWidth: { xs: '100%', md: 245 }, flexGrow: 1 }}
            />
            <Button variant="contained" startIcon={query.isFetching ? <CircularProgress size={16} /> : <RefreshRoundedIcon />}
              disabled={!referenceDate || query.isFetching} onClick={runAudit} sx={{ whiteSpace: 'nowrap', minHeight: 40 }}>
              {query.isFetching ? 'Analizando…' : 'Ejecutar auditoría'}
            </Button>
          </Stack>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.73rem' }}>
            Seleccionar una máquina sirve para resolver sus reglas específicas de escandallo; no implica que sea compatible con todas las órdenes.
          </Typography>
        </Stack>
      </Paper>

      {query.isError && <Alert severity="error" variant="outlined">
        No se ha podido ejecutar la auditoría. Verifica que el backend esté actualizado y que tu usuario disponga del permiso de planificación.
      </Alert>}
      {!requested && <Paper elevation={0} sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: '#cbd5e1', fontWeight: 800 }}>Preparado para analizar órdenes reales</Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.83rem', mt: 1 }}>
          Elige la fecha y, si procede, una máquina; pulsa «Ejecutar auditoría».
        </Typography>
      </Paper>}
      {response && <>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          {([
            ['Órdenes revisadas', response.summary.analyzed, '#7dd3fc'],
            ['Calculables', response.summary.calculable, '#86efac'],
            ['Bloqueadas', response.summary.blocked, '#fbbf24'],
          ] as const).map(([label, value, color]) => <Paper key={label} elevation={0} sx={{ ...cardSx, p: 1.7, flex: 1 }}>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.73rem', fontWeight: 750 }}>{label}</Typography>
            <Typography sx={{ color, fontSize: '1.55rem', fontWeight: 900 }}>{value}</Typography>
          </Paper>)}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              lg: 'minmax(0, 1.5fr) minmax(0, 1fr)',
            },
            gap: 1.5,
          }}
        >
          <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 0.5 }}>
              Diagnóstico de relaciones
            </Typography>

            <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', mb: 1.5 }}>
              Incidencias encontradas en las {response.summary.analyzed} órdenes examinadas.
              Selecciona un motivo para consultar las órdenes afectadas.
            </Typography>

            {issueFilter !== 'all' && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setIssueFilter('all');
                  setPage(0);
                  setExpanded([]);
                }}
                sx={{ mb: 1.5 }}
              >
                Quitar filtro de incidencia
              </Button>
            )}

            <Stack spacing={0.75} sx={{ maxHeight: 370, overflowY: 'auto', pr: 0.5 }}>
              {diagnostics.issues.length === 0 && (
                <Alert severity="success" variant="outlined">
                  No se han detectado incidencias en la muestra examinada.
                </Alert>
              )}

              {diagnostics.issues.map(issue => (
                <Button
                  key={issue.code}
                  fullWidth
                  variant={issueFilter === issue.code ? 'contained' : 'outlined'}
                  onClick={() => {
                    setIssueFilter(
                      issueFilter === issue.code ? 'all' : issue.code,
                    );
                    setStatusFilter('all');
                    setSearch('');
                    setPage(0);
                    setExpanded([]);
                  }}
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    gap: 2,
                    p: 1.2,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.83rem' }}>
                      {issue.message}
                    </Typography>

                    <Typography sx={{ fontSize: '0.7rem', opacity: 0.75 }}>
                      Ejemplos: {issue.examples.join(', ') || 'Sin OF identificable'}
                    </Typography>
                  </Box>

                  <Chip
                    label={issue.count}
                    size="small"
                    sx={{
                      fontWeight: 900,
                      bgcolor: 'rgba(56,189,248,0.15)',
                      color: '#7dd3fc',
                    }}
                  />
                </Button>
              ))}
            </Stack>

            {unresolvedBreakdown.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1.5, borderColor: 'rgba(148,163,184,0.18)' }} />

                <Typography sx={{ color: '#7dd3fc', fontWeight: 900, fontSize: '0.85rem', mb: 0.5 }}>
                  Desglose de productos no identificados
                </Typography>

                <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem', mb: 1.25 }}>
                  Diferenciamos los datos ausentes de las referencias que existen,
                  pero que el auditor no consigue relacionar con un producto.
                </Typography>

                <Stack spacing={0.85}>
                  {unresolvedBreakdown.map(group => (
                    <Box
                      key={group.label}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: 'rgba(148,163,184,0.06)',
                        border: '1px solid rgba(148,163,184,0.14)',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Typography sx={{ color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 800 }}>
                          {group.label}
                        </Typography>

                        <Chip
                          size="small"
                          label={group.count}
                          sx={{ color: '#7dd3fc', fontWeight: 900 }}
                        />
                      </Stack>

                      <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', mt: 0.65 }}>
                        Ejemplos: {group.examples.join(' | ')}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <Typography sx={{ color: '#64748b', fontSize: '0.7rem', mt: 1.25 }}>
              Una misma orden puede presentar varias incidencias.
              Los recuentos corresponden únicamente a la muestra analizada.
            </Typography>
          </Paper>

          <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 0.5 }}>
              Unidades de medida detectadas
            </Typography>

            <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', mb: 1.5 }}>
              Valores originales, sin conversiones automáticas.
            </Typography>

            <Typography sx={{ color: '#7dd3fc', fontWeight: 850, fontSize: '0.8rem', mb: 1 }}>
              Unidad recibida en las órdenes
            </Typography>

            <Stack spacing={0.65}>
              {diagnostics.sourceUnits.map(([unit, count]) => (
                <Stack
                  key={unit}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    bgcolor: 'rgba(148,163,184,0.06)',
                    borderRadius: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: '0.8rem' }}>{unit}</Typography>
                  <Chip size="small" label={count} />
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 1.5, borderColor: 'rgba(148,163,184,0.14)' }} />

            <Typography sx={{ color: '#7dd3fc', fontWeight: 850, fontSize: '0.8rem', mb: 1 }}>
              Unidad configurada en el catálogo
            </Typography>

            <Stack spacing={0.65}>
              {diagnostics.catalogUnits.map(([unit, count]) => (
                <Stack
                  key={unit}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    bgcolor: 'rgba(148,163,184,0.06)',
                    borderRadius: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: '0.8rem' }}>{unit}</Typography>
                  <Chip size="small" label={count} />
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>

        {response.summary.truncated && <Alert severity="info" variant="outlined">
          Se ha revisado una muestra de {response.summary.analyzed} órdenes sobre {response.summary.orders_total} candidatas.
          Estos totales no representan el conjunto completo. Amplía el límite para analizar más registros.
        </Alert>}
        <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
          <Stack spacing={1.25}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <TextField select size="small" label="Resultado" value={statusFilter}
                onChange={event => setStatusFilter(event.target.value as StatusFilter)} sx={{ minWidth: 175 }}>
                <MenuItem value="all">Todas las órdenes</MenuItem>
                <MenuItem value="calculable">Calculables</MenuItem>
                <MenuItem value="blocked">Bloqueadas</MenuItem>
              </TextField>
              <TextField size="small" label="Filtrar muestra cargada por OF, referencia, material o incidencia" value={search}
                onChange={event => setSearch(event.target.value)} sx={{ flex: 1 }} />
            </Stack>
            <Divider sx={{ borderColor: 'rgba(148,163,184,0.14)' }} />
            {rows.length === 0 && <Alert severity="info">No hay órdenes que coincidan con los filtros.</Alert>}
            {visibleRows.map((row, index) => {
              const rowKey = `${row.manufacturing_order_number}-${currentPage * rowsPerPage + index}`;
              const open = expanded.includes(rowKey);
              return <Box key={rowKey} sx={{ borderRadius: 2, border: '1px solid rgba(148,163,184,0.14)', overflow: 'hidden' }}>
                <Button fullWidth onClick={() => toggle(rowKey)}
                  endIcon={open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                  sx={{ display: 'flex', justifyContent: 'space-between', textAlign: 'left', p: 1.5, color: '#f8fafc', textTransform: 'none' }}>
                  <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '0.85rem' }}>
                      OF {row.manufacturing_order_number || 'Sin identificar'} · {row.product_ref || row.product_code || 'Sin referencia'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.73rem', color: '#94a3b8' }}>
                      {fmt(row.quantity_pending)} {row.quantity_unit || ''} pendientes · {row.materials.length} materias primas calculadas
                    </Typography>
                  </Box>
                  <Chip size="small" sx={{ ml: 1, mr: 1, color: row.status === 'calculable' ? '#86efac' : '#fbbf24',
                    border: `1px solid ${row.status === 'calculable' ? 'rgba(34,197,94,0.45)' : 'rgba(251,191,36,0.45)'}` }}
                  label={row.status === 'calculable' ? 'Calculable' : `${row.issues.length} incidencias`} />
                </Button>
                <Collapse in={open} unmountOnExit><Box sx={{ px: 1, pb: 1 }}>{detail(row)}</Box></Collapse>
              </Box>;
            })}

            <TablePagination
              component="div"
              count={rows.length}
              page={currentPage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Órdenes por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count} órdenes filtradas`
              }
              onPageChange={(_, newPage) => {
                setPage(newPage);
                setExpanded([]);
              }}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
                setExpanded([]);
              }}
              sx={{
                color: '#cbd5e1',
                borderTop: '1px solid rgba(148,163,184,0.14)',
                '& .MuiTablePagination-selectIcon': {
                  color: '#7dd3fc',
                },
                '& .MuiIconButton-root': {
                  color: '#7dd3fc',
                },
                '& .MuiIconButton-root.Mui-disabled': {
                  color: '#475569',
                },
              }}
            />
          </Stack>
        </Paper>
      </>}
    </Stack>
  );
}
